import { shortNumberNotationStartingWithLakhs } from '@cp/src/utils/commonUtils';
import { useMemo, useState } from 'react';
import ToggleTabs from '../toggleTabs/ToggleTabs';
import Typography from '@navi/web-ui/lib/primitives/Typography';
import styles from './CashCollectedChart.module.scss';
import cx from 'classnames';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { CashCollectedChartProps } from './interfaces';
import { firstDate } from './constants';
import { tabOptions, transformData } from './utils';
import { getDayOfMonth, getLastDate, VIEW_MODE } from '../leaderboard/const';

const CashCollectedChart = (props: CashCollectedChartProps) => {
  const { prevMonthData, currMonthData } = props;
  const [viewMode, setViewMode] = useState<VIEW_MODE>(VIEW_MODE.CURRENT);
  const totalPrevMonthCashCollected = prevMonthData?.totalCashCollected;
  const totalCurrentMonthCashCollected = currMonthData?.totalCashCollected;

  const handleViewChange = () =>
    setViewMode(prev => (prev == VIEW_MODE.CURRENT ? VIEW_MODE.PREVIOUS : VIEW_MODE.CURRENT));
  const totalCashCollected = useMemo(() => {
    return shortNumberNotationStartingWithLakhs(
      viewMode === VIEW_MODE.CURRENT ? totalCurrentMonthCashCollected : totalPrevMonthCashCollected
    );
  }, [totalPrevMonthCashCollected, totalCurrentMonthCashCollected, viewMode]);

  const data = useMemo(() => {
    const currMonth = [
      currMonthData?.foreclosuresCollectedBreakupResponse,
      currMonthData?.feesCollectedBreakupResponse,
      currMonthData?.emiCollectedBreakupResponse
    ];
    const prevMonth = [
      prevMonthData?.foreclosuresCollectedBreakupResponse,
      prevMonthData?.feesCollectedBreakupResponse,
      prevMonthData?.emiCollectedBreakupResponse
    ];
    return viewMode === VIEW_MODE.CURRENT
      ? currMonth?.map(transformData)
      : prevMonth?.map(transformData);
  }, [totalPrevMonthCashCollected, totalCurrentMonthCashCollected, viewMode]);

  const options = {
    chart: {
      plotBackgroundColor: null as any,
      plotBorderWidth: 0,
      plotShadow: false,
      renderTo: 'container',
      height: 200,
      width: 500
    },
    credits: {
      enabled: false
    },
    title: {
      text: `<Typography class='${styles.chartTitleContainer}'>₹${totalCashCollected}</Typography>`,
      align: 'center',
      verticalAlign: 'middle'
    },
    tooltip: {
      enabled: false,
      pointFormat: `<Typography class='${styles.tooltip}'>{point.percentage:.1f}%</Typography>`
    },
    accessibility: {
      point: {
        valueSuffix: '%'
      }
    },
    plotOptions: {
      pie: {
        size: 120,
        animation: true,
        startAngle: -85,
        colors: ['var(--chart-orange-dark)', 'var(--chart-green-light)', 'var(--chart-blue-dark)'],
        dataLabels: {
          enabled: true,
          align: 'left',
          format: `<Typography class=${styles.gray}>{point.name}</Typography class=${styles.darkGray}><div><span class=${styles.gray} >{point.name}</span><br/>{point.value} <span class=${styles.lightGrayText} >({point.percentage:.2f}%)</span></div>`,
          useHTML: true,
          distance: 10,
          filter: {
            property: 'percentage',
            operator: '>',
            value: 0
          }
        }
      }
    },
    series: [
      {
        type: 'pie',
        name: 'CashCollected',
        innerSize: '70%',
        data: data || [],
        states: {
          hover: {
            enabled: false
          },
          inactive: {
            enabled: false
          }
        }
      }
    ]
  };

  return (
    <div className={styles.pieChartWrapper}>
      <div className="flex flex-row-reverse mr-4">
        <ToggleTabs
          selected={viewMode}
          onTabClick={handleViewChange}
          tabOptions={tabOptions}
          size="h5"
          tabClass="!mt-0 !mb-0"
          activeTabClass={styles.boxShadow}
        />
        <Typography variant="p5" className="flex text-sm mr-20 mt-3 text-[var(--grayscale-1)] z-10">
          {viewMode === VIEW_MODE.CURRENT ? (
            <div>Last {getDayOfMonth()} days cash collected&nbsp;</div>
          ) : (
            <div>Cash collected last month&nbsp;</div>
          )}
          <div className={styles.darkGray}>
            {firstDate} - {getLastDate(viewMode)}
          </div>
        </Typography>
      </div>
      <div className={styles.pieChart}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};

export default CashCollectedChart;
